import React, { useMemo, useState } from 'react';
import { useApp } from '../context/AppContext';
import { formatCurrency, formatDateOnlyDisplay, formatMotTimeSlotDisplay, parseDateOnlyLocal } from '../utils/helpers';
import { useResponsive } from '../utils/useResponsive';
import { isStageEnabled, isVehicleWorkTypeVisible } from '../utils/enabledStages';

const GARAGE_STATUSES = ['MOT', 'Bodyshop', 'Workshop', 'Tyres'];
const IN_PROGRESS_STATUSES = ['In Progress', 'Awaiting Parts'];

const getGarageCategory = (veh) => {
  const status = veh.status || '';
  if (GARAGE_STATUSES.includes(status)) return status;
  if (IN_PROGRESS_STATUSES.includes(status)) return veh.previousStatus || 'Workshop';
  return null;
};

const toDateInputValue = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getWeekDateRange = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const start = new Date(now);
  const day = start.getDay();
  start.setDate(start.getDate() - day + (day === 0 ? -6 : 1));
  return { start: toDateInputValue(start), end: toDateInputValue(now) };
};

const getMonthDateRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: toDateInputValue(start), end: toDateInputValue(end) };
};

const GarageReport = ({ onOpenVehicleDetail }) => {
  const { vehicles, garageInfo } = useApp();
  const { isMobile, isTablet } = useResponsive();
  const isMobileView = isMobile || isTablet;
  const [activeFilter, setActiveFilter] = useState(null);
  const [daysFilter, setDaysFilter] = useState(null);
  const [periodFilter, setPeriodFilter] = useState(null);
  const [revenueStart, setRevenueStart] = useState('');
  const [revenueEnd, setRevenueEnd] = useState('');
  const [revenuePreset, setRevenuePreset] = useState(null);

  const calculateDaysInStock = (vehicle) => {
    const stockDate = vehicle.dropOffDate || vehicle.receivedDate;
    if (!stockDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const stock = parseDateOnlyLocal(stockDate);
    if (!stock) return null;
    const diffTime = today - stock;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 ? diffDays : 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateOnlyDisplay(dateString, {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }) || 'N/A';
  };

  const formatBookedDate = (timeSlot, fallbackDate) => {
    if (timeSlot) {
      const formatted = formatMotTimeSlotDisplay(timeSlot, fallbackDate);
      if (formatted) return formatted;
    }
    return formatDate(fallbackDate);
  };

  const getStartOfWeek = (d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    date.setDate(diff);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  };

  const getStartOfMonth = (d) => {
    const date = new Date(d);
    date.setDate(1);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  };

  const garageData = useMemo(() => {
    const garageVehicles = vehicles.filter((veh) => {
      if (veh.deletedAt) return false;
      const category = getGarageCategory(veh);
      if (category === null) return false;
      return isStageEnabled(garageInfo, category);
    });

    const stats = {
      totalVehicles: garageVehicles.length,
      motCount: 0,
      bodyshopCount: 0,
      workshopCount: 0,
      tyresCount: 0,
      totalCost: 0,
      totalPrepCost: 0,
      totalWarranty: 0,
      totalMisc: 0,
      motRevenue: 0,
      bodyshopRevenue: 0,
      workshopRevenue: 0,
      tyresRevenue: 0,
      vehicles: []
    };

    const getInvoicedTotal = (veh) => {
      const status = veh.status || '';
      if (!['Workshop', 'Bodyshop', 'MOT', 'Tyres'].includes(status) && !IN_PROGRESS_STATUSES.includes(status)) return veh.cost || 0;
      const category = getGarageCategory(veh);
      const lineItemsTotal = (veh.lineItems || []).reduce((sum, item) => sum + (parseFloat(item.cost) || 0), 0);
      const labourCost = parseFloat(veh.labourCost) || 0;
      const estimatedCost = veh.estimatedCost !== undefined && Number(veh.estimatedCost) > 0 ? Number(veh.estimatedCost) : 0;
      const subtotal = estimatedCost > 0 ? estimatedCost : (labourCost + lineItemsTotal);
      const vatEnabled = category === 'MOT' ? false : (veh.includeVat !== undefined ? veh.includeVat : true);
      const vat = vatEnabled ? subtotal * 0.2 : 0;
      return subtotal + vat;
    };

    garageVehicles.forEach((veh) => {
      const originalIndex = vehicles.findIndex((v) => v.reg === veh.reg);
      const cost = veh.cost || 0;
      const prepCost = veh.prepCost || 0;
      const warranty = veh.warranty || 0;
      const misc = veh.misc || 0;
      const totalCost = cost + prepCost + warranty + misc;
      const status = veh.status || '';
      const category = getGarageCategory(veh);
      const invoicedTotal = getInvoicedTotal(veh);

      if (category === 'MOT') {
        stats.motCount++;
        stats.motRevenue += invoicedTotal;
      }
      if (category === 'Bodyshop') {
        stats.bodyshopCount++;
        stats.bodyshopRevenue += invoicedTotal;
      }
      if (category === 'Workshop') {
        stats.workshopCount++;
        stats.workshopRevenue += invoicedTotal;
      }
      if (category === 'Tyres') {
        stats.tyresCount++;
        stats.tyresRevenue += invoicedTotal;
      }

      stats.totalCost += cost;
      stats.totalPrepCost += prepCost;
      stats.totalWarranty += warranty;
      stats.totalMisc += misc;

      const daysInStock = calculateDaysInStock(veh);
      const stockDate = veh.dropOffDate || veh.receivedDate;

      stats.vehicles.push({
        reg: veh.reg,
        vehicle: veh.vehicle,
        status,
        category,
        cost,
        prepCost,
        warranty,
        misc,
        totalCost,
        invoicedTotal,
        contact: veh.contact || 'N/A',
        originalIndex: originalIndex !== -1 ? originalIndex : null,
        dropOffDate: veh.dropOffDate || veh.receivedDate,
        receivedDate: veh.receivedDate || veh.dropOffDate,
        daysInStock,
        stockDate,
        timeSlot: veh.timeSlot || null
      });
    });

    stats.vehicles.sort((a, b) => {
      const dateA = a.stockDate;
      const dateB = b.stockDate;
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return new Date(dateB).getTime() - new Date(dateA).getTime();
    });

    return stats;
  }, [vehicles, garageInfo]);

  const filteredVehicles = useMemo(() => {
    let filtered = garageData.vehicles;

    if (activeFilter) {
      if (activeFilter === 'totalVehicles') {
        filtered = garageData.vehicles;
      } else {
        filtered = garageData.vehicles.filter((v) => v.category === activeFilter);
      }
    }

    if (daysFilter !== null) {
      filtered = filtered.filter((v) => v.daysInStock !== null && v.daysInStock >= daysFilter);
    }

    if (periodFilter) {
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime();
      const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime();
      filtered = filtered.filter((v) => {
        if (!v.stockDate) return false;
        const t = new Date(v.stockDate).getTime();
        if (periodFilter === 'day') {
          return t >= startOfToday && t <= endOfToday;
        }
        if (periodFilter === 'week') {
          const start = getStartOfWeek(now);
          return t >= start && t <= endOfToday;
        }
        if (periodFilter === 'month') {
          const start = getStartOfMonth(now);
          return t >= start && t <= endOfToday;
        }
        return true;
      });
    }

    return filtered;
  }, [garageData.vehicles, activeFilter, daysFilter, periodFilter]);

  const applyRevenuePreset = (preset) => {
    const range = preset === 'week' ? getWeekDateRange() : getMonthDateRange();
    setRevenuePreset(preset);
    setRevenueStart(range.start);
    setRevenueEnd(range.end);
  };

  const handleRevenueStartChange = (value) => {
    setRevenuePreset(null);
    setRevenueStart(value);
  };

  const handleRevenueEndChange = (value) => {
    setRevenuePreset(null);
    setRevenueEnd(value);
  };

  const clearRevenuePeriod = () => {
    setRevenuePreset(null);
    setRevenueStart('');
    setRevenueEnd('');
  };

  const revenueByArea = useMemo(() => {
    const inRange = (veh) => {
      if (!revenueStart && !revenueEnd) return true;
      if (!veh.stockDate) return false;
      const t = parseDateOnlyLocal(veh.stockDate);
      if (!t) return false;
      t.setHours(0, 0, 0, 0);
      if (revenueStart) {
        const start = parseDateOnlyLocal(revenueStart);
        if (start && t < start) return false;
      }
      if (revenueEnd) {
        const end = parseDateOnlyLocal(revenueEnd);
        if (end && t > end) return false;
      }
      return true;
    };

    const list = garageData.vehicles.filter(inRange);
    const byCategory = (category) => list.filter((v) => v.category === category);
    const sum = (rows) => rows.reduce((acc, v) => acc + (v.invoicedTotal || 0), 0);
    const mot = byCategory('MOT');
    const bodyshop = byCategory('Bodyshop');
    const workshop = byCategory('Workshop');
    const tyres = byCategory('Tyres');

    return {
      motCount: mot.length,
      motRevenue: sum(mot),
      bodyshopCount: bodyshop.length,
      bodyshopRevenue: sum(bodyshop),
      workshopCount: workshop.length,
      workshopRevenue: sum(workshop),
      tyresCount: tyres.length,
      tyresRevenue: sum(tyres),
      totalRevenue: sum(list)
    };
  }, [garageData.vehicles, revenueStart, revenueEnd]);

  const handleCardClick = (filterType) => {
    setActiveFilter(activeFilter === filterType ? null : filterType);
  };

  const clearFilters = () => {
    setActiveFilter(null);
    setDaysFilter(null);
    setPeriodFilter(null);
  };

  const getStatusStyle = (status) => {
    const styles = {
      MOT: { bg: 'rgba(86, 197, 150, 0.22)', color: '#279268' },
      Bodyshop: { bg: 'rgba(236, 72, 153, 0.2)', color: '#be185d' },
      Workshop: { bg: 'rgba(79, 141, 243, 0.18)', color: '#3f63b8' },
      Tyres: { bg: 'rgba(180, 83, 9, 0.2)', color: '#92400e' },
      'In Progress': { bg: 'rgba(245, 158, 11, 0.18)', color: '#b45309' },
      'Awaiting Parts': { bg: 'rgba(245, 158, 11, 0.18)', color: '#b45309' }
    };
    return styles[status] || { bg: 'var(--light-bg)', color: 'var(--text-dark)' };
  };

  const statKeyHandler = (fn) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fn();
    }
  };

  return (
    <section
      id="garageReportSection"
      className={`garage-report ${isMobileView ? 'garage-report--mobile' : ''}`}
    >
      <div className="dashboard-container garage-report-page">
        <div className="dashboard-left">
          <div className="dashboard-header">
            <p className="dashboard-kicker">Reports</p>
            <h1>Garage Report</h1>
            <p className="dashboard-subtitle">
              Complete breakdown of vehicles in Bodyshop, Workshop, MOT and Tyres — counts, revenue by area, and live stock.
            </p>
          </div>

          <div className="dashboard-stats-grid">
            <div
              className={`dashboard-stat-card clickable ${activeFilter === 'totalVehicles' ? 'card-filter-active' : ''}`}
              onClick={() => handleCardClick('totalVehicles')}
              role="button"
              tabIndex={0}
              onKeyDown={statKeyHandler(() => handleCardClick('totalVehicles'))}
            >
              <div className="stat-icon stat-icon--area-total">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-label">Total Vehicles</div>
                <div className="stat-value">{garageData.totalVehicles}</div>
                {activeFilter === 'totalVehicles' && (
                  <div className="garage-report-filter-hint">
                    Showing {filteredVehicles.length} vehicle{filteredVehicles.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>

            {isStageEnabled(garageInfo, 'MOT') && (
            <div
              className={`dashboard-stat-card clickable ${activeFilter === 'MOT' ? 'card-filter-active' : ''}`}
              onClick={() => handleCardClick('MOT')}
              role="button"
              tabIndex={0}
              onKeyDown={statKeyHandler(() => handleCardClick('MOT'))}
            >
              <div className="stat-icon stat-icon--area-mot">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-label">MOT</div>
                <div className="stat-value">{garageData.motCount}</div>
                {activeFilter === 'MOT' && (
                  <div className="garage-report-filter-hint">
                    Showing {filteredVehicles.length} vehicle{filteredVehicles.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
            )}

            {isStageEnabled(garageInfo, 'Bodyshop') && (
            <div
              className={`dashboard-stat-card clickable ${activeFilter === 'Bodyshop' ? 'card-filter-active' : ''}`}
              onClick={() => handleCardClick('Bodyshop')}
              role="button"
              tabIndex={0}
              onKeyDown={statKeyHandler(() => handleCardClick('Bodyshop'))}
            >
              <div className="stat-icon stat-icon--area-bodyshop">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-label">Bodyshop</div>
                <div className="stat-value">{garageData.bodyshopCount}</div>
                {activeFilter === 'Bodyshop' && (
                  <div className="garage-report-filter-hint">
                    Showing {filteredVehicles.length} vehicle{filteredVehicles.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
            )}

            {isStageEnabled(garageInfo, 'Workshop') && (
            <div
              className={`dashboard-stat-card clickable ${activeFilter === 'Workshop' ? 'card-filter-active' : ''}`}
              onClick={() => handleCardClick('Workshop')}
              role="button"
              tabIndex={0}
              onKeyDown={statKeyHandler(() => handleCardClick('Workshop'))}
            >
              <div className="stat-icon stat-icon--area-workshop">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-label">Workshop</div>
                <div className="stat-value">{garageData.workshopCount}</div>
                {activeFilter === 'Workshop' && (
                  <div className="garage-report-filter-hint">
                    Showing {filteredVehicles.length} vehicle{filteredVehicles.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
            )}

            {isStageEnabled(garageInfo, 'Tyres') && (
            <div
              className={`dashboard-stat-card clickable ${activeFilter === 'Tyres' ? 'card-filter-active' : ''}`}
              onClick={() => handleCardClick('Tyres')}
              role="button"
              tabIndex={0}
              onKeyDown={statKeyHandler(() => handleCardClick('Tyres'))}
            >
              <div className="stat-icon stat-icon--area-tyres">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <circle cx="12" cy="12" r="8" />
                </svg>
              </div>
              <div className="stat-content">
                <div className="stat-label">Tyres</div>
                <div className="stat-value">{garageData.tyresCount}</div>
                {activeFilter === 'Tyres' && (
                  <div className="garage-report-filter-hint">
                    Showing {filteredVehicles.length} vehicle{filteredVehicles.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
            )}
          </div>

          <div className="dashboard-financial-section">
            <div className="garage-report-panel garage-report-revenue">
              <h2 className="section-title">Revenue by area</h2>
              <p className="garage-report-section-desc">
                Revenue (cost charged) per area so you can see which part of the business is performing best.
              </p>
              <div className="garage-report-revenue-filters">
                <div className="garage-report-revenue-presets">
                  <button
                    type="button"
                    className={`report-filter-btn ${revenuePreset === 'week' ? 'active' : ''}`}
                    onClick={() => applyRevenuePreset('week')}
                  >
                    Week
                  </button>
                  <button
                    type="button"
                    className={`report-filter-btn ${revenuePreset === 'month' ? 'active' : ''}`}
                    onClick={() => applyRevenuePreset('month')}
                  >
                    Month
                  </button>
                </div>
                <label className="garage-report-filter-field">
                  <span>Start date</span>
                  <input
                    type="date"
                    className="garage-report-select"
                    value={revenueStart}
                    max={revenueEnd || undefined}
                    onChange={(e) => handleRevenueStartChange(e.target.value)}
                    aria-label="Revenue start date"
                  />
                </label>
                <label className="garage-report-filter-field">
                  <span>End date</span>
                  <input
                    type="date"
                    className="garage-report-select"
                    value={revenueEnd}
                    min={revenueStart || undefined}
                    onChange={(e) => handleRevenueEndChange(e.target.value)}
                    aria-label="Revenue end date"
                  />
                </label>
                {(revenueStart || revenueEnd) && (
                  <button type="button" className="hero-button" onClick={clearRevenuePeriod}>
                    All dates
                  </button>
                )}
              </div>
              <div className="financial-cards garage-report-revenue-cards">
                {isStageEnabled(garageInfo, 'MOT') && (
                <div className="financial-card">
                  <div className="financial-label">MOT</div>
                  <div className="financial-value">{formatCurrency(revenueByArea.motRevenue)}</div>
                  <div className="financial-subtitle">{revenueByArea.motCount} vehicle{revenueByArea.motCount !== 1 ? 's' : ''}</div>
                </div>
                )}
                {isStageEnabled(garageInfo, 'Bodyshop') && (
                <div className="financial-card">
                  <div className="financial-label">Bodyshop</div>
                  <div className="financial-value">{formatCurrency(revenueByArea.bodyshopRevenue)}</div>
                  <div className="financial-subtitle">{revenueByArea.bodyshopCount} vehicle{revenueByArea.bodyshopCount !== 1 ? 's' : ''}</div>
                </div>
                )}
                {isStageEnabled(garageInfo, 'Workshop') && (
                <div className="financial-card">
                  <div className="financial-label">Workshop</div>
                  <div className="financial-value">{formatCurrency(revenueByArea.workshopRevenue)}</div>
                  <div className="financial-subtitle">{revenueByArea.workshopCount} vehicle{revenueByArea.workshopCount !== 1 ? 's' : ''}</div>
                </div>
                )}
                {isStageEnabled(garageInfo, 'Tyres') && (
                <div className="financial-card">
                  <div className="financial-label">Tyres</div>
                  <div className="financial-value">{formatCurrency(revenueByArea.tyresRevenue)}</div>
                  <div className="financial-subtitle">{revenueByArea.tyresCount} vehicle{revenueByArea.tyresCount !== 1 ? 's' : ''}</div>
                </div>
                )}
              </div>
              <div className="garage-report-revenue-total">
                <span className="garage-report-revenue-total-label">Total revenue</span>
                <span className="garage-report-revenue-total-value">{formatCurrency(revenueByArea.totalRevenue)}</span>
              </div>
            </div>
          </div>

          <div className="dashboard-financial-section">
            <div className="garage-report-panel garage-report-breakdown" style={{ marginBottom: 0 }}>
              <div className="garage-report-breakdown-header">
                <h2 className="section-title garage-report-breakdown-title">
                  Vehicle breakdown
                  {(activeFilter || daysFilter !== null || periodFilter) && (
                    <span className="garage-report-filter-count">
                      ({filteredVehicles.length} of {garageData.vehicles.length})
                    </span>
                  )}
                </h2>
                <div className="garage-report-filters">
                  <label className="garage-report-filter-field">
                    <span>Period</span>
                    <select
                      className="garage-report-select"
                      value={periodFilter || ''}
                      onChange={(e) => setPeriodFilter(e.target.value === '' ? null : e.target.value)}
                      aria-label="Filter by period"
                    >
                      <option value="">All</option>
                      <option value="day">Today</option>
                      <option value="week">This week</option>
                      <option value="month">This month</option>
                    </select>
                  </label>
                  <label className="garage-report-filter-field">
                    <span>Days on site</span>
                    <select
                      className="garage-report-select"
                      value={daysFilter !== null ? daysFilter : ''}
                      onChange={(e) => setDaysFilter(e.target.value === '' ? null : parseInt(e.target.value, 10))}
                      aria-label="Filter by days on site"
                    >
                      <option value="">All</option>
                      <option value="30">30+ days</option>
                      <option value="60">60+ days</option>
                      <option value="90">90+ days</option>
                      <option value="120">120+ days</option>
                    </select>
                  </label>
                  {(activeFilter || daysFilter !== null || periodFilter) && (
                    <button type="button" className="hero-button" onClick={clearFilters}>
                      Clear filters
                    </button>
                  )}
                </div>
              </div>

              {garageData.vehicles.length === 0 ? (
                <div className="empty-state">No vehicles in Bodyshop, Workshop or MOT.</div>
              ) : filteredVehicles.length === 0 ? (
                <div className="empty-state">No vehicles match the selected filter.</div>
              ) : isMobileView ? (
                <div className="garage-report-mobile-list">
                  {filteredVehicles.map((veh, idx) => {
                    const statusStyle = getStatusStyle(veh.status);
                    const categoryStyle = veh.category ? getStatusStyle(veh.category) : null;
                    return (
                      <div
                        key={`${veh.reg}-${idx}`}
                        role="button"
                        tabIndex={0}
                        className="garage-report-mobile-card"
                        onClick={() => {
                          if (onOpenVehicleDetail && veh.originalIndex !== null) {
                            onOpenVehicleDetail(veh.originalIndex);
                          }
                        }}
                        onKeyDown={(e) => {
                          if ((e.key === 'Enter' || e.key === ' ') && onOpenVehicleDetail && veh.originalIndex !== null) {
                            e.preventDefault();
                            onOpenVehicleDetail(veh.originalIndex);
                          }
                        }}
                      >
                        <div className="garage-report-mobile-card-header">
                          <span className="garage-report-mobile-card-reg">{veh.reg}</span>
                          <span className="garage-report-mobile-card-cost">{formatCurrency(veh.invoicedTotal)}</span>
                        </div>
                        <div className="garage-report-mobile-card-vehicle">{veh.vehicle}</div>
                        <div className="garage-report-mobile-card-meta">
                          {veh.category && (
                            <span
                              className="garage-report-mobile-card-badge"
                              style={{ background: categoryStyle.bg, color: categoryStyle.color }}
                            >
                              {veh.category}
                            </span>
                          )}
                          {veh.status !== veh.category && veh.status && (
                            <span
                              className="garage-report-mobile-card-badge"
                              style={{ background: statusStyle.bg, color: statusStyle.color }}
                            >
                              {veh.status}
                            </span>
                          )}
                          <span className="garage-report-mobile-card-days">
                            {veh.daysInStock !== null ? (
                              <span
                                style={{
                                  color: veh.daysInStock >= 90 ? '#EF4444' : veh.daysInStock >= 60 ? '#F59E0B' : 'var(--text-dark)',
                                  fontWeight: 600
                                }}
                              >
                                {veh.daysInStock} {veh.daysInStock === 1 ? 'day' : 'days'}
                              </span>
                            ) : (
                              'N/A'
                            )}
                          </span>
                        </div>
                        <div className="garage-report-mobile-card-row">
                          <span className="garage-report-mobile-card-label">Booked in</span>
                          <span className="garage-report-mobile-card-value">
                            {veh.category === 'MOT'
                              ? formatBookedDate(veh.timeSlot, veh.stockDate)
                              : formatDate(veh.stockDate)}
                          </span>
                        </div>
                        <div className="garage-report-mobile-card-row">
                          <span className="garage-report-mobile-card-label">Contact</span>
                          <span className="garage-report-mobile-card-value">{veh.contact}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="garage-report-table-wrap">
                  <table className="garage-report-table">
                    <thead>
                      <tr>
                        <th>Reg</th>
                        <th>Vehicle</th>
                        <th>Status</th>
                        <th>Booked in</th>
                        <th className="garage-report-th-center">Days</th>
                        <th className="garage-report-th-numeric">Total cost</th>
                        <th>Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredVehicles.map((veh, idx) => {
                        const statusStyle = getStatusStyle(veh.status);
                        const clickable = onOpenVehicleDetail && veh.originalIndex !== null;
                        return (
                          <tr
                            key={`${veh.reg}-${idx}`}
                            className={clickable ? 'garage-report-row-clickable' : ''}
                            onClick={() => {
                              if (clickable) onOpenVehicleDetail(veh.originalIndex);
                            }}
                          >
                            <td className="garage-report-td-reg">{veh.reg}</td>
                            <td>{veh.vehicle}</td>
                            <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                              {veh.category && (
                                <span
                                  className="garage-report-badge"
                                  style={{
                                    background: getStatusStyle(veh.category).bg,
                                    color: getStatusStyle(veh.category).color
                                  }}
                                >
                                  {veh.category}
                                </span>
                              )}
                              {veh.status !== veh.category && veh.status && (
                                <span
                                  className="garage-report-badge"
                                  style={{
                                    background: statusStyle.bg,
                                    color: statusStyle.color
                                  }}
                                >
                                  {veh.status}
                                </span>
                              )}
                            </td>
                            <td className="garage-report-td-muted">
                              {veh.category === 'MOT'
                                ? formatBookedDate(veh.timeSlot, veh.stockDate)
                                : formatDate(veh.stockDate)}
                            </td>
                            <td className="garage-report-td-center">
                              {veh.daysInStock !== null ? (
                                <span
                                  className="garage-report-days-pill"
                                  style={{
                                    color: veh.daysInStock >= 90 ? '#EF4444' : veh.daysInStock >= 60 ? '#F59E0B' : 'var(--text-dark)',
                                    background: veh.daysInStock >= 90 ? 'rgba(239, 68, 68, 0.1)' : veh.daysInStock >= 60 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(79, 141, 243, 0.1)'
                                  }}
                                >
                                  {veh.daysInStock} {veh.daysInStock === 1 ? 'day' : 'days'}
                                </span>
                              ) : (
                                <span className="garage-report-td-muted">N/A</span>
                              )}
                            </td>
                            <td className="garage-report-td-numeric">{formatCurrency(veh.invoicedTotal)}</td>
                            <td className="garage-report-td-muted">{veh.contact}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GarageReport;
